import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useAbility } from '@casl/react';
import SvgIcon from '../../../../common/components/SvgIcon';
import SurveyBuilderAgent from '../../../../common/agent/SurveyBuilderAgent';
import { AbilityContext } from '../../../../common/Can';
import { VIEW, SURVEY_BUILDER_INTERNAL } from '../../../../common/ability';

/* "Built with AI" panel on the survey edit page (sdesregistry/IH-Engage#2543): shows when
 * a builder session is behind this poll — who built it, when it was imported, the counts,
 * how many reviews are submitted — and, for GMV-team roles, the way into the review
 * screen (the Survey Builder in review mode, same full-page navigation as "Create with
 * AI"). Renders nothing while loading or when the server says there is nothing. */

function formatWhen(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function reviewUrl(projectId, pollId) {
  const base = process.env.REACT_APP_SURVEY_BUILDER_URL || '';
  return `${base}/survey-builder/?projectId=${encodeURIComponent(projectId)}&review=1&pollId=${encodeURIComponent(
    pollId,
  )}&internal=true`;
}

function AiSessionPanel({ projectId, pollId }) {
  const ability = useAbility(AbilityContext);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setInfo(null);
    if (!projectId || !pollId) return undefined;
    SurveyBuilderAgent.aiSession(projectId, pollId)
      .then((data) => {
        if (!cancelled) setInfo(data);
      })
      .catch(() => {
        /* a failed lookup is not an error the editor needs to see — no panel */
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, pollId]);

  if (!info) return null;

  const canReview = info.canReview && ability.can(VIEW, SURVEY_BUILDER_INTERNAL);
  const imported = formatWhen(info.importedAt);
  const who = info.user && info.user.name;
  const facts = [
    imported ? `imported ${imported}` : null,
    who ? `by ${who}` : null,
    `${info.turns} turn${info.turns === 1 ? '' : 's'}`,
    `${info.drafts} draft${info.drafts === 1 ? '' : 's'}`,
    info.reviewedCount > 0
      ? `${info.reviewedCount} review${info.reviewedCount === 1 ? '' : 's'} submitted`
      : 'not yet reviewed',
  ].filter(Boolean);

  return (
    <div className='c-message c-message--icon is-information' data-testid='ai-session-panel'>
      <SvgIcon icon='star-filled' />
      <p className='c-message__description'>
        <strong>Built with AI</strong>
        {` · ${facts.join(' · ')}`}
        {canReview && (
          <button
            className='o-button o-button--primary o-button--xs'
            type='button'
            data-testid='ai-session-review'
            onClick={() => window.location.assign(reviewUrl(projectId, pollId))}
          >
            <span className='o-label'>Review AI session</span>
          </button>
        )}
      </p>
    </div>
  );
}

AiSessionPanel.propTypes = {
  projectId: PropTypes.string.isRequired,
  pollId: PropTypes.string.isRequired,
};

export default AiSessionPanel;
